import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input, Textarea } from '@redpanda/ui';
import FormFieldWithError from './forms/FormFieldWithError';
import { createFiscalYear, updateFiscalYear } from '../services/api';
import { toast } from 'react-hot-toast';
import { useFiscalYearStoreBase } from '../stores/fiscalYearStore';

// 使用函數來建立 schema，這樣可以使用 t()
const createFiscalYearSchema = (t: any) => z.object({
  name: z.string().min(1, t('periods.fiscalYearDialog.validation.nameRequired')),
  startDate: z.string().min(1, t('periods.fiscalYearDialog.validation.startDateRequired')),
  endDate: z.string().min(1, t('periods.fiscalYearDialog.validation.endDateRequired')),
  description: z.string().optional(),
}).refine((data) => {
  const start = new Date(data.startDate);
  const end = new Date(data.endDate);
  return start < end;
}, {
  message: t('periods.fiscalYearDialog.validation.startBeforeEnd'),
  path: ['endDate'],
});

// 型別由 schema 推導，與 zodResolver 一致（手寫型別在 strict: false 下會和 zod 推導結果不相容）
type FiscalYearFormData = z.infer<ReturnType<typeof createFiscalYearSchema>>;

interface FiscalYearDialogProps {
  isOpen: boolean;
  onClose: () => void;
  fiscalYear?: any;
  onFiscalYearCreated: (savedFiscalYear?: any, isEdit?: boolean) => void;
}

const FiscalYearDialog: React.FC<FiscalYearDialogProps> = ({
  isOpen,
  onClose,
  fiscalYear,
  onFiscalYearCreated
}) => {
  const { t } = useTranslation();
  const isEditing = !!fiscalYear;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FiscalYearFormData>({
    resolver: zodResolver(createFiscalYearSchema(t)),
    defaultValues: {
      name: '',
      startDate: '',
      endDate: '',
      description: '',
    },
  });

  useEffect(() => {
    if (isOpen) {
      if (isEditing && fiscalYear) {
        // 編輯模式：填入現有數據
        reset({
          name: fiscalYear.name || '',
          startDate: fiscalYear.startDate ? fiscalYear.startDate.split('T')[0] : '',
          endDate: fiscalYear.endDate ? fiscalYear.endDate.split('T')[0] : '',
          description: fiscalYear.description || '',
        });
      } else {
        // 新增模式：重置表單
        const currentYear = new Date().getFullYear();
        reset({
          name: String(currentYear),
          startDate: `${currentYear}-01-01`,
          endDate: `${currentYear}-12-31`,
          description: '',
        });
      }
    }
  }, [isOpen, isEditing, fiscalYear, reset]);

  const onSubmit = async (data: FiscalYearFormData) => {
    if (isEditing) {
      // UPDATE 樂觀更新
      const originalFiscalYear = { ...fiscalYear };
      const optimisticFiscalYear = {
        ...fiscalYear,
        ...data,
        startDate: new Date(data.startDate).toISOString(),
        endDate: new Date(data.endDate).toISOString(),
        updatedAt: new Date().toISOString(),
      };

      useFiscalYearStoreBase.getState().updateItem(fiscalYear.id, optimisticFiscalYear);
      onClose();
      onFiscalYearCreated(optimisticFiscalYear, true);

      try {
        const response = await updateFiscalYear(fiscalYear.id, data);
        const realData = response.data || optimisticFiscalYear;
        useFiscalYearStoreBase.getState().updateItem(fiscalYear.id, realData);
        toast.success(t('common.messages.updateSuccess'));
      } catch (error: any) {
        useFiscalYearStoreBase.getState().updateItem(fiscalYear.id, originalFiscalYear);
        const msg = error.response?.data?.error || error.response?.data?.message || error.message || t('periods.fiscalYearDialog.toast.updateFailed');
        toast.error(msg);
      }
    } else {
      // CREATE 樂觀更新
      const tempId = `temp-${Date.now()}`;
      const tempFiscalYear = {
        id: tempId,
        organizationId: '',
        name: data.name,
        startDate: new Date(data.startDate).toISOString(),
        endDate: new Date(data.endDate).toISOString(),
        isActive: false,
        isClosed: false,
        description: data.description,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      useFiscalYearStoreBase.getState().addItem(tempFiscalYear as any);
      onClose();
      onFiscalYearCreated(tempFiscalYear as any, false);

      try {
        const response = await createFiscalYear(data);
        const realData = response.data || tempFiscalYear;
        useFiscalYearStoreBase.getState().updateItem(tempId, realData);
        toast.success(t('common.messages.createSuccess'));
      } catch (error: any) {
        useFiscalYearStoreBase.setState((state: any) => ({
          items: state.items.filter((i: any) => i.id !== tempId),
          pagination: state.pagination ? {
            ...state.pagination,
            totalItems: Math.max(0, state.pagination.totalItems - 1),
          } : null,
        }));
        const msg = error.response?.data?.error || error.response?.data?.message || error.message || t('periods.fiscalYearDialog.toast.createFailed');
        toast.error(msg);
      }
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
      onMouseDown={handleBackgroundClick}
    >
      <div 
        className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center px-6 py-3 border-b border-gray-200 dark:border-slate-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {isEditing ? t('periods.fiscalYearDialog.title.edit') : t('periods.fiscalYearDialog.title.add')}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* 對話框內容 */}
        <div className="p-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 overflow-visible">

            <FormFieldWithError
              label={t('periods.fiscalYearDialog.fields.name')}
              required={true}
              error={errors.name?.message}
              htmlFor="name"
            >
              <Input
                id="name"
                type="text"
                {...register('name')}
                variant={errors.name ? "error" : "default"}
                placeholder={t('periods.fiscalYearDialog.placeholder.name')}
              />
            </FormFieldWithError>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormFieldWithError
                label={t('periods.fiscalYearDialog.fields.startDate')}
                required={true}
                error={errors.startDate?.message}
                htmlFor="startDate"
              >
                <Input
                  id="startDate"
                  type="date"
                  {...register('startDate')}
                  variant={errors.startDate ? "error" : "default"}
                />
              </FormFieldWithError>

              <FormFieldWithError
                label={t('periods.fiscalYearDialog.fields.endDate')}
                required={true}
                error={errors.endDate?.message}
                htmlFor="endDate"
              >
                <Input
                  id="endDate"
                  type="date"
                  {...register('endDate')}
                  variant={errors.endDate ? "error" : "default"}
                />
              </FormFieldWithError>
            </div>

            <FormFieldWithError
              label={t('periods.fiscalYearDialog.fields.description')}
              required={false}
              error={errors.description?.message}
              htmlFor="description"
            >
              <Textarea
                id="description"
                rows={3}
                {...register('description')}
                variant={errors.description ? "error" : "default"}
                placeholder={t('periods.fiscalYearDialog.placeholder.description')}
              />
            </FormFieldWithError>

            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse pt-6 border-t border-gray-200 dark:border-slate-700">
              <button
                type="submit"
                className="w-full sm:w-auto sm:ml-3 bg-brand-600 hover:bg-brand-700 text-white font-bold py-2 px-4 rounded transition-all duration-200"
              >
                {isEditing ? t('periods.fiscalYearDialog.actions.update') : t('periods.fiscalYearDialog.actions.create')}
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="mt-3 w-full sm:mt-0 sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-md"
              >
                {t('periods.fiscalYearDialog.actions.cancel')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FiscalYearDialog;